import React, { useState } from 'react';
import { 
  Grid3X3, 
  Layout, 
  ArrowRight, 
  Zap, 
  Settings, 
  BarChart3,
  Wallet,
  Target,
  Layers,
  Code2,
  Sparkles,
  Lock
} from 'lucide-react';

interface PlatformSelectionProps {
  onGoHome: () => void;
  onSelectCanvasUI: () => void;
  onSelectModules: () => void;
  walletAddress?: string;
  walletProvider?: 'phantom' | 'solflare';
  onShowConnectModal?: () => void;
  onDisconnectWallet?: () => void;
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({ 
  onGoHome, 
  onSelectCanvasUI,
  onSelectModules,
  walletAddress,
  walletProvider,
  onShowConnectModal,
  onDisconnectWallet
}) => {
  const [hoveredOption, setHoveredOption] = useState<'canvas' | 'modules' | null>(null);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col justify-center items-center py-8 pt-24 relative overflow-hidden">
      {/* Architectural Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Main background image with reduced opacity */}
        <img 
          src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/213.png"
          alt="Background"
          className="w-full h-full object-cover opacity-10"
        />
        
        {/* Architectural Lines Grid */}
        <div className="absolute inset-0">
          {/* Horizontal lines */}
          <div className="absolute top-1/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-300/60 to-transparent"></div>
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b]/80 to-transparent"></div>
          <div className="absolute top-3/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-300/60 to-transparent"></div>
          
          {/* Vertical lines */}
          <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-300/60 to-transparent"></div>
          <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-300/60 to-transparent"></div>
          
          {/* Cross decorations */}
          <div className="absolute top-1/4 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
            <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
            <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          </div>
          <div className="absolute top-1/4 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
            <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
            <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          </div>
        </div>
        
        {/* Subtle green glow orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 rounded-full bg-[#24c35b]/10 blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-gray-300/20 blur-[80px] animate-pulse delay-1000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-8">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 glassmorphism rounded-2xl flex items-center justify-center mr-4">
              <Zap className="w-8 h-8 text-[#24c35b]" />
            </div>
            <div>
              <h1 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight">
                Select Your
                <span className="block text-[#24c35b]">Experience</span>
              </h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Choose how you want to interact with EKKO.TRADE platform
          </p>
          
          {/* Connection Status */}
          {walletAddress && (
            <div className="glassmorphism p-4 rounded-lg border border-white/20 max-w-md mx-auto mt-8">
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-[#24c35b] mr-2 animate-pulse"></div>
                <span className="text-gray-700 text-sm font-medium">
                  {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Platform Options Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* CanvasUI Option */}
          <div 
            className={`glassmorphism p-8 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex flex-col ${
              hoveredOption === 'canvas' 
                ? 'border-[#24c35b]/60 shadow-[0_0_30px_rgba(36,195,91,0.3)]' 
                : 'border-gray-200/80 hover:border-[#24c35b]/40'
            }`}
            onMouseEnter={() => setHoveredOption('canvas')}
            onMouseLeave={() => setHoveredOption(null)}
            onClick={onSelectCanvasUI}
          >
            <div className="text-center flex-1 flex flex-col">
              {/* Icon Container */}
              <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                hoveredOption === 'canvas' 
                  ? 'bg-[#24c35b]/20 shadow-lg' 
                  : 'bg-gray-100'
              }`}>
                <Layout className={`w-10 h-10 transition-colors duration-300 ${
                  hoveredOption === 'canvas' ? 'text-[#24c35b]' : 'text-gray-600'
                }`} />
              </div>
              
              {/* Title */}
              <h2 className="text-3xl font-black text-gray-900 mb-4">CanvasUI</h2>
              
              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                Full-featured trading interface with customizable widgets, advanced charts, and complete trading tools
              </p>
              
              {/* Features List */}
              <div className="space-y-3 mb-8 flex-1">
                <div className="flex items-center justify-center text-sm text-gray-700">
                  <BarChart3 className="w-4 h-4 text-[#24c35b] mr-2" />
                  Advanced Charts & Analytics
                </div>
                <div className="flex items-center justify-center text-sm text-gray-700">
                  <Layers className="w-4 h-4 text-[#24c35b] mr-2" />
                  Customizable Widget Layout
                </div>
                <div className="flex items-center justify-center text-sm text-gray-700">
                  <Target className="w-4 h-4 text-[#24c35b] mr-2" />
                  Professional Trading Tools
                </div>
              </div>
              
              {/* Action Button */}
              <button 
                className="btn btn-primary btn-lg w-full group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span>Launch CanvasUI</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>

          {/* Modules Option - DISABLED */}
          <div 
            className="glassmorphism p-8 rounded-2xl border-2 opacity-50 cursor-not-allowed flex flex-col border-gray-200/40 relative"
          >
            {/* Lock Icon Overlay */}
            <div className="absolute top-4 right-4 z-10">
              <div className="w-8 h-8 bg-gray-400/80 rounded-lg flex items-center justify-center">
                <Lock className="w-4 h-4 text-white" />
              </div>
            </div>
            
            <div className="text-center flex-1 flex flex-col">
              {/* Icon Container */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-gray-100">
                <Grid3X3 className="w-10 h-10 text-gray-400" />
              </div>
              
              {/* Title */}
              <h2 className="text-3xl font-black text-gray-400 mb-4">Modules</h2>
              
              {/* Description */}
              <p className="text-gray-400 mb-6 leading-relaxed">
                Individual focused tools and pages for specific trading functions and portfolio management
              </p>
              
              {/* Features List */}
              <div className="space-y-3 mb-8 flex-1">
                <div className="flex items-center justify-center text-sm text-gray-400">
                  <Code2 className="w-4 h-4 text-gray-400 mr-2" />
                  Dedicated Tool Pages
                </div>
                <div className="flex items-center justify-center text-sm text-gray-400">
                  <Settings className="w-4 h-4 text-gray-400 mr-2" />
                  Simplified Interface
                </div>
                <div className="flex items-center justify-center text-sm text-gray-400">
                  <Sparkles className="w-4 h-4 text-gray-400 mr-2" />
                  Focused Experience
                </div>
              </div>
              
              {/* Disabled Button */}
              <button 
                className="btn bg-gray-300/50 text-gray-400 border-gray-300/50 btn-lg w-full cursor-not-allowed"
                disabled
              >
                <span className="flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span>Coming Soon</span>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-16">
          <div className="glassmorphism p-6 rounded-xl border border-gray-200/80 max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome to EKKO</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Start with CanvasUI for the complete trading experience. Modules will be available soon with focused access to individual tools.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformSelection; 